import React from 'react';
import { Check, AlertTriangle } from 'lucide-react';
import { useTranslation } from '../../i18n/useTranslation';
import { tokens } from '../../styles/designTokens';
import StandardButton from './StandardButton';

// ===========================================================================
// NoticeStrip
//
// The one non-modal notice surface: a thin, in-flow strip rendered above the
// view switch, carrying one short message, an optional action, and a dismiss.
//
// This is the generalisation of what SignInNotice used to be. That component
// held three hard-coded statuses and a header saying a fourth must never be
// added — the next unrelated message was to be the moment the surface itself
// got designed. That moment arrived with the demo expired-session notice
// (docs/briefs/2026-09-25-demo-read-expired-session.md + addendum 1), and the
// Architect's ruling was to generalise rather than special-case: message key,
// tone and optional action become props, and SignInNotice becomes a caller that
// maps its three statuses onto them with rendering identical to before.
//
// So the old header's rule is honoured, not overridden: there is still no
// fourth special-case status anywhere. What changed is that the surface now has
// a shape a second caller can use without editing it.
//
// It is still NOT a notification system — no queue, no timer, no stacking. One
// strip at a time, owned by whoever renders it. If two messages ever need to be
// live at once, that is a queue, and a queue is a design decision, not an extra
// prop.
//
// Presentational only (Architecture.md § chrome components): props in, no
// fetching, no auth reads. Strings resolve through t(), so it must be rendered
// inside <TranslationProvider> — which is what keeps TRANSLATION.md's scoped
// App.js pre-provider carve-out at exactly the size it is documented to be.
//
// LAYOUT.md has no § Notices section; its absence is recorded in
// docs/doc-accuracy-register.md (25 Sep 2026) and the text is owed through the
// #52 ratification batch. Until it lands, this file is the de-facto description
// of the surface: in-flow (so no z-index tier is consumed), accent colour from
// an existing semantic token, and the action is a StandardButton per
// LAYOUT.md § Usage Rule :350-356.
//
// Both buttons pass their words as `label` (the semantic API). Until 16 Oct 2026 they passed
// `text`, which the semantic variants ignore, so the action and Close rendered blank (found while
// building Roadmap #103's Undo closure).
// ===========================================================================

// Tone drives icon and accent colour only. Colours come from existing semantic
// tokens — there is no info/success token in designTokens.js, and a literal here
// would have made a tenth copy of the #EFF6FF/#1E40AF info-banner triple already
// recorded in docs/reuse-backlog.md.
// 'warning' (Roadmap #103, a group being closed) uses notificationAlert, LAYOUT's documented
// Help/Warning hue: a state the reader should know about, not a failure.
const TONES = {
  success: { Icon: Check,          colour: tokens.colors.action },
  error:   { Icon: AlertTriangle,  colour: tokens.colors.destructive },
  warning: { Icon: AlertTriangle,  colour: tokens.colors.notificationAlert }
};

/**
 * @param {string}  messageKey - t() key for the message
 * @param {string}  [namespace='auth'] - translation namespace the key lives in
 * @param {Object}  [messageParams] - interpolation values for the message (e.g. { date })
 * @param {'success'|'error'|'warning'} [tone='error']
 * @param {{labelKey: string, onClick: Function, disabled?: boolean}} [action] - optional primary action
 * @param {Function} [onDismiss] - omit to render no dismiss control
 */
const NoticeStrip = ({ messageKey, namespace = 'auth', messageParams, tone = 'error', action, onDismiss }) => {
  const { t } = useTranslation(namespace);

  if (!messageKey) return null;

  const { Icon, colour } = TONES[tone] || TONES.error;

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        padding: '10px 16px',
        borderBottom: `2px solid ${colour}`,
        background: '#FFFFFF',
        minWidth: '320px',
        boxSizing: 'border-box'
      }}
    >
      <Icon size={18} color={colour} style={{ flexShrink: 0 }} />
      <span style={{ flex: 1, fontSize: '14px', lineHeight: 1.4, color: '#1F2937' }}>
        {t(messageKey, messageParams)}
      </span>
      {action && (
        <StandardButton
          variant="primary"
          label={t(action.labelKey)}
          onClick={action.onClick}
          disabled={!!action.disabled}
        />
      )}
      {onDismiss && (
        <StandardButton
          variant="ghost"
          label={t('common:close')}
          onClick={onDismiss}
        />
      )}
    </div>
  );
};

export default NoticeStrip;
